import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

import type { ImageQuote } from "../../../api/flowTypes";
import { canStart, quoteText, shortfallText } from "../../../model/generateFlow";

/**
 * AI 配图报价确认:「将生成约 N 张图,约 M 积分」+ 单价 + 当前余额;按实际成功张数扣,失败的不扣。
 * ★ 余额不足:说清差多少,「确认生成」禁用 —— 不开始任务。
 */
export function QuoteConfirmDialog({
  quote,
  busy,
  onConfirm,
  onCancel,
  title = "确认生成官网",
  confirmText = "确认生成",
  description,
}: {
  quote: ImageQuote | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  confirmText?: string;
  /** 换掉默认说明(按张计费那句会接在后面)。 */
  description?: string;
}) {
  const ok = canStart(quote);
  return (
    <Dialog open={quote !== null} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-md" data-quote-dialog="">
        <DialogHeader>
          <DialogTitle className="normal-case tracking-normal">{title}</DialogTitle>
          <DialogDescription>
            {description ? `${description}。` : ""}
            AI 配图按实际生成成功的张数扣积分,没生成成功的不扣。
          </DialogDescription>
        </DialogHeader>
        {quote && (
          <div className="space-y-2 text-sm">
            <p className="font-medium text-foreground">{quoteText(quote)}</p>
            <p className="text-xs text-muted-foreground">
              单价 {quote.unitCredits} 积分 / 张 · 当前余额 {quote.availableCredits} 积分
            </p>
            <p className="text-2xs text-muted-foreground">
              共 {quote.totalSlots} 个图片位:你的图 {quote.galleryCount} · 产品图{" "}
              {quote.productImageCount} · AI 生成 {quote.aiCount} · 装饰图标{" "}
              {quote.templateFallbackCount}
            </p>
            {!ok && (
              <p role="alert" className="text-xs text-destructive">
                {shortfallText(quote)}。可以先关掉「AI 配图」生成,或充值后再来。
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
            取消
          </Button>
          <Button type="button" disabled={!ok || busy} onClick={onConfirm}>
            {busy && <Loader2 className="animate-spin" />}
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
